/**
 * Cookie Consent Storage
 * Manages consent choices in localStorage
 */

const CONSENT_STORAGE_KEY = 'cellcog_cookie_consent';
// Bumped to 2.0 for the global consent rollout (v2). Bumping invalidates all
// existing 1.0 choices so every user re-consents under the new worldwide model.
const CONSENT_VERSION = '2.0';

/**
 * Get consent data from localStorage
 * @returns {Object|null} Consent data or null if not found
 */
export const getConsentData = () => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Verify version matches (invalidate old versions)
    if (data.version !== CONSENT_VERSION) {
      clearConsentData();
      return null;
    }

    return data;
  } catch (error) {
    console.error('[CookieConsent] Error reading consent data:', error);
    return null;
  }
};

/**
 * Save consent data to localStorage
 * @param {Object} consentData - Consent choices and metadata
 */
export const saveConsentData = (consentData) => {
  try {
    const dataToStore = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      ...consentData,
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(dataToStore));
  } catch (error) {
    console.error('[CookieConsent] Error saving consent data:', error);
  }
};

/**
 * Clear consent data from localStorage
 */
export const clearConsentData = () => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    console.error('[CookieConsent] Error clearing consent data:', error);
  }
};

/**
 * Check if user has made a consent choice
 * @returns {boolean} True if consent choice exists
 */
export const hasConsentChoice = () => {
  const data = getConsentData();
  return data !== null && data.choices !== undefined;
};

/**
 * Get analytics consent status
 * @returns {boolean} True if analytics is consented
 */
export const getAnalyticsConsent = () => {
  const data = getConsentData();
  return data?.choices?.analytics === true;
};

/**
 * Update only analytics consent (preserving other data)
 * @param {boolean} analyticsConsent - New analytics consent value
 */
export const updateAnalyticsConsent = (analyticsConsent) => {
  const existing = getConsentData() || { choices: {} };

  saveConsentData({
    ...existing,
    choices: {
      essential: true, // Always true
      analytics: analyticsConsent,
    },
    lastUpdated: new Date().toISOString(),
  });
};

/**
 * May CellCog's own Support chat count this visitor's clicks WITH its visitor id? (Nov 2 2026, the chat window's click
 * tracking; the chat is told through CellCog('consent', bool).) No under Global Privacy Control, and no when analytics are
 * declined: a recorded "no" anywhere, or the strict-consent bucket (isEU) before any choice, where analytics stay denied
 * until one. Take 2 (review 8): a region not known yet (geo detection still running, isEU null) is a no until it is. Yes
 * only for the quiet bucket's default. A "no" is still counted, only without an id.
 * @param {Object} state - the CookieConsentContext values ({ consentGiven, analyticsConsent, isEU }); {} outside the provider
 * @param {boolean} gpc - navigator.globalPrivacyControl
 * @returns {boolean}
 */
export const supportChatConsent = (state = {}, gpc = typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true) => {
  if (gpc) return false;
  if (state.consentGiven) return state.analyticsConsent === true;
  const stored = getConsentData();
  if (stored && stored.choices) return stored.choices.analytics === true;
  return state.isEU === false;
};

/**
 * Banner ignore-cooldown (geo-split v3, Aug 24): an IGNORED banner used to
 * return on every visit forever — 75% of viewers never click, so they were
 * nagged indefinitely. Now a reveal stamps a timestamp; while it is fresh
 * (7 days) the banner stays hidden for visitors who have not chosen. Legally
 * safe for the opt-in bucket: analytics stays DENIED until an actual choice,
 * the cooldown only limits how often we re-ask.
 */
const BANNER_SHOWN_KEY = 'cellcog_cookie_banner_last_shown';
const BANNER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export const markBannerShown = () => {
  try {
    localStorage.setItem(BANNER_SHOWN_KEY, String(Date.now()));
  } catch (error) {
    console.error('[CookieConsent] Error stamping banner shown:', error);
  }
};

export const isBannerInCooldown = () => {
  try {
    const t = Number(localStorage.getItem(BANNER_SHOWN_KEY));
    return !!t && Date.now() - t < BANNER_COOLDOWN_MS;
  } catch {
    return false;
  }
};

/**
 * Reset consent and clear cache (for testing)
 * Call in browser console: window.resetCookieConsent()
 */
export const resetCookieConsent = () => {
  clearConsentData();
  localStorage.removeItem('cellcog_geo_cache');
  localStorage.removeItem(BANNER_SHOWN_KEY);
};

// Expose to window for easy testing
if (typeof window !== 'undefined') {
  window.resetCookieConsent = resetCookieConsent;
  window.viewCookieConsent = () => {
    const data = getConsentData();
    return data;
  };
}
